import { useQuery } from '@tanstack/react-query'
import {
  ACCESS_CONTROL_CONFIG,
  getAccessibleCustomerIds,
  hasCustomerAccess,
} from '@/data/config/accessControl'
import { apiService } from '@/data/services/api/apiService'
import { CACHE_TIMES } from '@/data/services/api/cacheConfig'
import { endpoints } from '@/data/services/api/endpoints'
import { authKeys, useAuthStatus } from './useAuthStatus'

/**
 * The signed-in user's permission profile from GET /auth/me:
 * primary_role, has_all_access and accessible_customer_ids.
 *
 * Keyed by username so one user's profile is never served to the next, and
 * invalidated with the rest of the ['auth'] family on token refresh. Admin
 * features gate on has_all_access only; role names and groups are not trusted.
 *
 * @param {object} options - React Query options
 */
export function useUserRole(options = {}) {
  const { data: authStatus, isPending: authPending } = useAuthStatus()
  const username = authStatus?.user?.username ?? null

  const profileQuery = useQuery({
    queryKey: authKeys.profile(username),
    queryFn: () => apiService.get(endpoints.auth.userProfile),
    enabled: !!username,
    staleTime: CACHE_TIMES.SHORT_LIVED, // matches the server-side profile cache
    refetchOnWindowFocus: false,
    meta: { errorTitle: 'Failed to load user permissions' },
    ...options,
  })

  const userProfile = profileQuery.data ?? null
  const hasAllAccess = userProfile?.has_all_access === true
  const accessibleCustomerIds = Array.isArray(userProfile?.accessible_customer_ids)
    ? userProfile.accessible_customer_ids
    : []
  const hasAnyAccess = () => hasAllAccess || accessibleCustomerIds.length > 0

  return {
    userProfile,
    primaryRole: userProfile?.primary_role ?? null,
    hasAllAccess,
    accessibleCustomerIds,

    isLoading: authPending || (!!username && profileQuery.isPending),
    error: profileQuery.error,

    getAccessibleCustomers: () => getAccessibleCustomerIds(userProfile),
    canAccessCustomer: (customerId) => hasCustomerAccess(userProfile, customerId),
    isAdmin: () => hasAllAccess,
    hasAnyAccess,
    getNoAccessError: () =>
      hasAnyAccess()
        ? null
        : {
            message: ACCESS_CONTROL_CONFIG.noAccessErrorMessage,
            type: 'no-access',
            showContactSupport: true,
          },

    refetch: profileQuery.refetch,
  }
}
